import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Text, Title } from '@constants/Themed';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { AntDesign, MaterialCommunityIcons } from '@expo/vector-icons';
import apis from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

const LeaveManagement = () => {
  const theme = themeSelector();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);

  const { data: leaveRequestsData, isLoading } = useQuery({
    queryKey: ['hrLeaveRequests', currentCompany?._id, filterStatus],
    queryFn: async () => {
      const response = await apis.getLeaveRequests({ status: filterStatus });
      return response.data;
    },
    enabled: !!currentCompany?._id
  });

  const updateLeaveMutation = useMutation({
    mutationFn: ({ requestId, status }: { requestId: string; status: string }) =>
      apis.updateLeaveRequest(requestId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['hrDashboard'] });
    }
  });

  const leaveRequests = leaveRequestsData || [];

  const handleApprove = (requestId: string) => {
    updateLeaveMutation.mutate({ requestId, status: 'approved' });
  };

  const handleReject = (requestId: string) => {
    updateLeaveMutation.mutate({ requestId, status: 'rejected' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  return (
    <DefaultScreen>
      <View style={styles.container}>
        <View style={styles.header}>
          <Title style={{ fontSize: 28, fontWeight: '700', color: theme ? '#111827' : '#f9fafb' }}>
            Leave Management
          </Title>
        </View>

        {/* Filter */}
        <View style={styles.filterContainer}>
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterStatus === undefined && { backgroundColor: '#3b82f6' }
            ]}
            onPress={() => setFilterStatus(undefined)}
          >
            <Text style={styles.filterText}>All</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterStatus === 'pending' && { backgroundColor: '#f59e0b' }
            ]}
            onPress={() => setFilterStatus('pending')}
          >
            <Text style={styles.filterText}>Pending</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterStatus === 'approved' && { backgroundColor: '#10b981' }
            ]}
            onPress={() => setFilterStatus('approved')}
          >
            <Text style={styles.filterText}>Approved</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterStatus === 'rejected' && { backgroundColor: '#ef4444' }
            ]}
            onPress={() => setFilterStatus('rejected')}
          >
            <Text style={styles.filterText}>Rejected</Text>
          </TouchableOpacity>
        </View>

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <Text>Loading...</Text>
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            {leaveRequests.map((request: any) => (
              <View
                key={request._id}
                style={[
                  styles.requestCard,
                  {
                    backgroundColor: theme ? '#ffffff' : '#1f2937',
                    borderColor: theme ? '#e5e7eb' : '#374151'
                  }
                ]}
              >
                <View style={styles.requestHeader}>
                  <View style={styles.requestInfo}>
                    <Text style={[styles.requestTitle, { color: theme ? '#111827' : '#f9fafb' }]}>
                      Leave Request
                    </Text>
                    <Text style={[styles.requestDate, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                      {new Date(request.startDate).toLocaleDateString()} - {new Date(request.endDate).toLocaleDateString()}
                    </Text>
                  </View>
                  <View style={[
                    styles.statusBadge,
                    { backgroundColor: `${getStatusColor(request.status)}20` }
                  ]}>
                    <Text style={[
                      styles.statusText,
                      { color: getStatusColor(request.status) }
                    ]}>
                      {request.status}
                    </Text>
                  </View>
                </View>

                <View style={styles.requestDetails}>
                  <View style={styles.detailRow}>
                    <MaterialCommunityIcons name="calendar" size={16} color={theme ? '#6b7280' : '#9ca3af'} />
                    <Text style={[styles.detailText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                      Type: {request.leaveType}
                    </Text>
                  </View>
                  {request.reason && (
                    <View style={styles.reasonContainer}>
                      <Text style={[styles.reasonLabel, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                        Reason:
                      </Text>
                      <Text style={[styles.reasonText, { color: theme ? '#111827' : '#f9fafb' }]}>
                        {request.reason}
                      </Text>
                    </View>
                  )}
                </View>

                {request.status === 'pending' && (
                  <View style={styles.actionButtons}>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.approveButton]}
                      onPress={() => handleApprove(request._id)}
                      disabled={updateLeaveMutation.isPending}
                    >
                      <AntDesign name="check" size={16} color="#fff" />
                      <Text style={styles.actionButtonText}>Approve</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.rejectButton]}
                      onPress={() => handleReject(request._id)}
                      disabled={updateLeaveMutation.isPending}
                    >
                      <AntDesign name="close" size={16} color="#fff" />
                      <Text style={styles.actionButtonText}>Reject</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            ))}
            {leaveRequests.length === 0 && (
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                  No leave requests found
                </Text>
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20
  },
  header: {
    marginBottom: 24
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 20
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#e5e7eb'
  },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827'
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40
  },
  requestCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12
  },
  requestHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12
  },
  requestInfo: {
    flex: 1
  },
  requestTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4
  },
  requestDate: {
    fontSize: 14
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize'
  },
  requestDetails: {
    marginBottom: 12
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8
  },
  detailText: {
    fontSize: 14,
    marginLeft: 8,
    textTransform: 'capitalize'
  },
  reasonContainer: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#f3f4f6',
    borderRadius: 8
  },
  reasonLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4
  },
  reasonText: {
    fontSize: 14
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6
  },
  approveButton: {
    backgroundColor: '#10b981'
  },
  rejectButton: {
    backgroundColor: '#ef4444'
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600'
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center'
  },
  emptyText: {
    fontSize: 16
  }
});

export default LeaveManagement;

